import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	AfterViewInit,
	ElementRef,
	AfterContentInit,
} from "@angular/core";
import { AddressAutoCompleteOption } from "./address-auto-complete.model";
import { FormControl } from "@angular/forms";
import { debounceTime, filter } from "rxjs/operators";
import { addressRestrictionKeysOnly } from "app/shared";

@Component({
    selector: "dodo-address-auto-complete",
    templateUrl: "./address-auto-complete.component.html",
    styleUrls: ["./address-auto-complete.component.scss"],
    standalone: false
})
export class AddressAutoCompleteComponent implements OnInit, AfterContentInit {
	// Public Variables
	public _addressRestrictionKeysOnly = addressRestrictionKeysOnly;
	public activeOptionIndex = -1;
	public queryVal = "";
	public myControl = new FormControl("", {});
	public inputValue = "";
	// Private Variables
	private debounceTimeValue = 250;
	// Input Variables
	@Input() placeholder = "Type here";
	@Input() placeholderImageUrl: string;
	@Input() placeholderImageSize = 14;
	@Input() queryThreshold = 1;
	@Input() addresses: AddressAutoCompleteOption[] = [];
	@Input() noSuggestionsFound = false;
	@Input() busy = false;
	@Input() showManualEntryOption = true;
	@Input() showHintError = false;
	// Output Variables
	@Output() updateOptions = new EventEmitter<string>();
	@Output() optionSelected = new EventEmitter<AddressAutoCompleteOption>();
	@Output() clear = new EventEmitter();
	// Element Variables
	@ViewChild("addressInput", { static: true }) addressInput: ElementRef;

	// Constructor
	constructor() {}

	// OnInit
	ngOnInit() {
		const input = this.myControl.valueChanges.pipe(
			debounceTime(this.debounceTimeValue)
		);

		this.myControl.valueChanges.subscribe((value) => {
			this.inputValue = value;
		});

		const suggest = input.pipe(
			filter(
				(val) => val.trim().length >= this.queryThreshold || val === ""
			)
		);

		const clearSuggestions = input.pipe(
			filter((val) => val.length < this.queryThreshold)
		);

		suggest.subscribe((val) => {
			this.queryVal = val;
			this.updateOptions.emit(val);
		});

		clearSuggestions.subscribe(() => {
			this.clear.emit();
		});
	}

	// Content Init
	ngAfterContentInit() {
		this.addressInput.nativeElement.focus();
	}

	public get errorMessage(): string {
		if (this.myControl.errors) {
			if (this.myControl.hasError("required")) {
				return `Address is required`;
			}
		}
	}

	public validationImage() {
		if (this.myControl.dirty && !this.myControl.errors) {
			return "/assets/img/icons/icon-tick-green.svg";
		} else {
			return "";
		}
	}

	public autoCompleteAddressSelected(
		selectedOption: AddressAutoCompleteOption
	) {
		this.activeOptionIndex = -1;
		this.queryVal = "";
		if (selectedOption) {
			this.myControl.setValue(selectedOption.Address, {
				emitEvent: false,
			});
			this.optionSelected.emit(selectedOption);
		} else {
			this.optionSelected.emit(null);
			this.myControl.setValue("", { emitEvent: false });
		}
	}

	public onKeydown(event: KeyboardEvent) {
		if (event.key === "ArrowUp") {
			event.preventDefault();
			if (this.activeOptionIndex > 0) {
				this.activeOptionIndex--;
			} else {
				this.activeOptionIndex = this.addresses.length;
			}
		} else if (event.key === "ArrowDown") {
			event.preventDefault();
			if (this.activeOptionIndex < this.addresses.length) {
				this.activeOptionIndex++;
			} else {
				this.activeOptionIndex = 0;
			}
		} else if (event.key === "Enter") {
			if (this.activeOptionIndex >= 0) {
				event.preventDefault();
				if (this.activeOptionIndex <= this.addresses.length - 1) {
					this.autoCompleteAddressSelected(
						this.addresses[this.activeOptionIndex]
					);
				} else {
					this.autoCompleteAddressSelected(null);
				}
			}
		}
	}

	// Hide and show addresses list
	public showAddressList(): boolean {
		return (
			this.addresses?.length > 0 ||
			(this.inputValue?.length > 0 && !this.busy)
		);
	}
}
